import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateInvitation, setPasswordFromInvite } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      const response = await validateInvitation(token);
      setInviteData(response.data);
      setName(response.data.user_name || '');
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setError('Invitation udløbet');
      } else {
        setError('Invitation ikke fundet');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 8) {
      setFormError('Password skal være mindst 8 tegn');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords matcher ikke');
      return;
    }

    setSubmitting(true);
    try {
      await setPasswordFromInvite(token, {
        password,
        name: name || undefined,
        phone: phone || undefined
      });
      setSuccess(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Kunne ikke oprette password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{error}</h2>
            <p className="text-slate-500 mb-6">Kontakt din administrator for at få en ny invitation.</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Gå til login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-outfit">
              Velkommen!
            </h2>
            <p className="text-slate-500 mb-6">Din adgang er nu oprettet.</p>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="go-to-login-btn"
            >
              Gå til login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-white font-outfit">SC</span>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 font-outfit">
            Opret din adgang
          </CardTitle>
          <CardDescription>
            {inviteData?.user_email}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {formError && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{formError}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name">Navn</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dit navn"
                className="h-12"
                data-testid="invite-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon (valgfri)</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+45 12 34 56 78"
                className="h-12"
                data-testid="invite-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindst 8 tegn"
                required
                className="h-12"
                data-testid="invite-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekræft password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Gentag password"
                required
                className="h-12"
                data-testid="invite-confirm-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              disabled={submitting}
              data-testid="invite-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opretter...
                </>
              ) : (
                'Opret adgang'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitePage;
